"use client";

import React, { useCallback, useMemo } from "react";
import { cn } from "@/lib/utils";
import type { DateGroupingGranularity, NumberRangeConfig } from "@/lib/utils/crm/grouping-strategies";

interface GroupingOptionsSelectorProps {
  fieldType: string;
  fieldLabel: string;
  groupingOptions?: {
    dateGranularity?: DateGroupingGranularity;
    numberRanges?: NumberRangeConfig;
  };
  onDateGranularityChange?: (granularity: DateGroupingGranularity) => void;
  onNumberRangesChange?: (ranges: NumberRangeConfig) => void;
  className?: string;
}

/**
 * Component for selecting grouping options (date granularity, number ranges)
 * Follows React best practices with memoization and useCallback
 */
export const GroupingOptionsSelector = React.memo(function GroupingOptionsSelector({
  fieldType,
  fieldLabel,
  groupingOptions,
  onDateGranularityChange,
  onNumberRangesChange,
  className,
}: GroupingOptionsSelectorProps) {
  // Memoize date granularity options
  const dateGranularityOptions = useMemo(
    () => [
      { value: "day" as const, label: "By Day" },
      { value: "month" as const, label: "By Month" },
      { value: "year" as const, label: "By Year" },
    ],
    []
  );

  // Memoize number range presets
  const numberRangePresets = useMemo(
    () => ({
      hours: {
        label: "Hours (0-1, 1-2, 2-3, 3-4, 4-10, 10+)",
        ranges: [
          { min: 0, max: 1, label: "0-1 hours" },
          { min: 1, max: 2, label: "1-2 hours" },
          { min: 2, max: 3, label: "2-3 hours" },
          { min: 3, max: 4, label: "3-4 hours" },
          { min: 4, max: 10, label: "4-10 hours" },
          { min: 10, max: null, label: "10+ hours" },
        ],
      },
      default: {
        label: "Default (0, 1-9, 10-49, 50-99, 100-499, 500-999, 1000+)",
        ranges: [
          { min: 0, max: 0, label: "0" },
          { min: 1, max: 9, label: "1-9" },
          { min: 10, max: 49, label: "10-49" },
          { min: 50, max: 99, label: "50-99" },
          { min: 100, max: 499, label: "100-499" },
          { min: 500, max: 999, label: "500-999" },
          { min: 1000, max: null, label: "1000+" },
        ],
      },
    }),
    []
  );

  // Memoize handlers with useCallback
  const handleDateGranularityChange = useCallback(
    (granularity: DateGroupingGranularity) => {
      onDateGranularityChange?.(granularity);
    },
    [onDateGranularityChange]
  );

  const handleNumberRangePresetChange = useCallback(
    (presetKey: string) => {
      const preset = numberRangePresets[presetKey as keyof typeof numberRangePresets];
      if (preset) {
        onNumberRangesChange?.({ ranges: preset.ranges });
      }
    },
    [numberRangePresets, onNumberRangesChange]
  );

  // Show date granularity options for date fields
  if (fieldType === "date" || fieldType === "datetime") {
    return (
      <div
        className={cn(
          "flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 dark:bg-blue-950/40 dark:border-blue-800 rounded-lg",
          className
        )}
      >
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
          Group {fieldLabel} by:
        </label>
        <div className="flex gap-2">
          {dateGranularityOptions.map((option) => (
            <button type="button"
              key={option.value}
              onClick={() => handleDateGranularityChange(option.value)}
              className={cn(
                "px-3 py-1.5 text-sm font-medium rounded transition-colors",
                groupingOptions?.dateGranularity === option.value
                  ? "bg-blue-600 text-white"
                  : "bg-card text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-700 hover:bg-card-50"
              )}
              title={`Group by ${option.label.toLowerCase()}`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Show number range presets for number fields
  if (fieldType === "number") {
    // Memoize preset comparison to avoid expensive JSON.stringify on every render
    const isHoursSelected = useMemo(() => {
      if (!groupingOptions?.numberRanges?.ranges) return false;
      const ranges = groupingOptions.numberRanges.ranges;
      return (
        ranges.length === 6 &&
        ranges[0].min === 0 &&
        ranges[0].max === 1 &&
        ranges[5].min === 10 &&
        ranges[5].max === null
      );
    }, [groupingOptions?.numberRanges?.ranges]);

    return (
      <div
        className={cn(
          "flex items-center gap-3 p-3 bg-green-50 border border-green-200 dark:bg-green-950/40 dark:border-green-800 rounded-lg",
          className
        )}
      >
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
          Group {fieldLabel} by:
        </label>
        <div className="flex gap-2">
          {Object.entries(numberRangePresets).map(([key, preset]) => (
            <button type="button"
              key={key}
              onClick={() => handleNumberRangePresetChange(key)}
              className={cn(
                "px-3 py-1.5 text-sm font-medium rounded transition-colors whitespace-nowrap",
                (key === "hours" && isHoursSelected) ||
                  (key === "default" && !isHoursSelected && groupingOptions?.numberRanges?.ranges)
                  ? "bg-green-600 text-white"
                  : "bg-card text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-700 hover:bg-card-50"
              )}
              title={preset.label}
            >
              {key === "hours" ? "Hours" : "Default"}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // No options for other field types
  return null;
});

GroupingOptionsSelector.displayName = "GroupingOptionsSelector";
